import { useEffect, useId, useState } from "react";
import { type CSSObject } from "@emotion/react";
import { IonDatetime, IonDatetimeButton, IonModal } from "@ionic/react";
import { DateTime } from "luxon";
import { Colors } from "main";

import { convertDateTimeToTimeString, convertStringToDT } from "./utils";

type TimeInputProps = {
  value: DateTime;
  onChange: (newValue: DateTime) => void;
  /**
   * Disabled the input and grey out the text.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Sets a minimum time that can be edited in
   * @default undefined
   */
  min?: DateTime;
  /**
   * Sets a maximum time that can be edited in
   * @default undefined
   */
  max?: DateTime;
  /**
   * Renders a non-editable version of the time input
   * @default false
   */
  readOnly?: boolean;
};

/**
 * Input | Time
 *
 * Renders a input text field when on Desktop and a native time picker on Mobile.
 *
 * TODO: Use as default time input
 * https://linear.app/swing-education/issue/MKTP-698
 */
export function TimeInput(props: TimeInputProps) {
  const { value, min, max, onChange, isDisabled, readOnly } = props;

  /*** Constants ***/
  // Generates a unique ID for the IonModal.
  const modalId = useId();

  /*** State ***/
  // Temporary state to store the edits made on the time input.
  const [timeString, setTimeString] = useState(convertDateTimeToTimeString(value));

  /*** Effect ***/
  // Update the time string when the prop value changes.
  useEffect(() => {
    if (convertDateTimeToTimeString(value) !== timeString) {
      setTimeString(convertDateTimeToTimeString(value));
    }
    // Only run this effect when the `value` prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /*** Handlers ***/
  function handleTextChange(newTimeString: string) {
    const newDateTime = convertStringToDT({ timeStr: newTimeString, dtObj: value, min, max });

    // If the new time string is valid, and is different from the current value,
    // pass the value to the parent.
    if (newDateTime && newDateTime !== value) {
      return onChange(newDateTime);
    }

    // Otherwise reset the value
    return setTimeString(convertDateTimeToTimeString(value));
  }

  /*** Read only Render ***/
  if (readOnly) {
    return timeString;
  }

  /*** Render ***/
  return (
    <>
      {/* Desktop */}
      <input
        type="text"
        // Limit to only support XX:XX XX
        maxLength={8}
        value={timeString}
        disabled={isDisabled}
        // Handle the text change
        onChange={(e) => setTimeString(e.target.value)}
        // Changes are complete when the user presses enter or blurs the input.
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Remove the focus from the input.
            // Which will trigger the `onBlur` handler.
            (e.target as HTMLInputElement).blur();
          }
        }}
        onBlur={() => handleTextChange(timeString)}
        css={{
          // TODO: Update this to match Desktop/Mobile breakpoint.
          // We are using the existing values to not break the current functionality.
          "@media (max-width: 992px)": { display: "none" },

          fontSize: 16,
          fontWeight: 400,
          borderRadius: 8,
          background: Colors.slate200,
          border: "none",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...STYLES,
          ...(isDisabled && DISABLED_STYLES),
        }}
      />

      {/* Mobile */}
      <div
        css={{
          // TODO: Update this to match Desktop/Mobile breakpoint.
          // We are using the existing values to not break the current functionality.
          "@media (min-width: 992px)": { display: "none" },
        }}
      >
        {/* TODO: Potentially mode this to using `useModal` hook, however, this
        would require us to no longer use `IonDateTimeButton`. */}
        <IonModal keepContentsMounted className="stackedModal" backdropDismiss={false}>
          <IonDatetime
            showDefaultButtons
            className="lowercase"
            id={modalId}
            presentation="time"
            value={value.toISO()}
            onIonChange={(ev: CustomEvent) => {
              onChange(DateTime.fromISO(ev.detail.value));
            }}
          />
        </IonModal>
        <IonDatetimeButton
          datetime={modalId}
          disabled={isDisabled}
          css={{
            // To change the background color of the input itself.
            // See https://ionicframework.com/docs/api/datetime-button#css-shadow-parts
            "&::part(native)": {
              backgroundColor: Colors.slate200,
              // To lowercase the AM/PM
              textTransform: "lowercase",
              ...STYLES,
              ...(isDisabled && DISABLED_STYLES),
            },
          }}
        />
      </div>
    </>
  );
}

/***** Helpers *****/
/**
 * Shared styles for the input and the IonDatetimeButton
 */
const STYLES: CSSObject = {
  // TODO: The following styles do not match the Figma design due to the
  // `IonDateTimeButton` component using these styles. To resolve this,
  // we must update the `IonDatetimeButton` as well.
  width: "100%",
  maxWidth: 90,
  margin: 0,
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 0,
  paddingBottom: 0,
  lineHeight: "24px",
  textAlign: "right",
  outline: "none",
  whiteSpace: "nowrap",
};
const DISABLED_STYLES: CSSObject = {
  color: Colors.slate400,
};
